import { Chip, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import TableLoader from 'components/common/TableLoader';
import { Alert } from 'components/common/Alert';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
// constants, reducers, graphql, styles
import {
  HTTP_STATUS,
  ACTIVE_TEXT,
  INACTIVE_TEXT,
  TABS,
  TabEnum,
  LOADING_TABLE_ROWS,
  EDIT_BOTTLE_SIZE_ROUTE,
  BOTTLE_SIZE_TABLE_HEADER,
} from 'constants/index';
import {
  BottleSizesPayload,
  useFindAllBottleSizeLazyQuery,
  BottleSizeProductTypesPayload,
  useUpdateBottleSizeStatusMutation,
} from 'generated/graphql';
import { formatBottleSizeProductTypes } from 'lib/helper';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import { Action, ActionType, State, initialState, bottleSizeReducer } from 'reducer/bottleSizeReducer';

const BottleSizeTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(bottleSizeReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab } = state;

  const [findAllBottleSize, { loading: fetchLoading, error }] = useFindAllBottleSizeLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllBottleSize } = data;
      const { pagination, response, data: bottleSizeData } = findAllBottleSize || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: bottleSizeData as BottleSizesPayload['data'] });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateBottleSizeStatus, { loading: updateLoading }] = useUpdateBottleSizeStatusMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateBottleSizeStatus } = data || {};
      const { response } = updateBottleSizeStatus || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchBottleSize();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchBottleSize = useCallback(async () => {
    await findAllBottleSize({
      variables: {
        findAllBottleSizesInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search,
          ...(activeTab !== TabEnum.All && { isActive: activeTab === TabEnum.Active }),
        },
      },
    });
  }, [findAllBottleSize, search, rowsPerPage, page, activeTab]);

  useEffect(() => {
    (!search.length || search.length > 2) && fetchBottleSize();
  }, [fetchBottleSize, search.length]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as TabEnum });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateBottleSizeStatus({
      variables: {
        updateBottleSizeStatusInput: {
          id,
          isActive: !status,
        },
      },
    });
  };

  const loading = fetchLoading || updateLoading;

  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={TABS} tabHandler={tabHandler} />

      <TableSearchField
        search={search}
        onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
        onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
      />

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={BOTTLE_SIZE_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={7} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const { id, name, isActive, lidSize, cc, oz, bottleSizeProductTypes } = cell || {};
              // make product type comma separated string
              const productType = formatBottleSizeProductTypes(
                bottleSizeProductTypes
                  ? (bottleSizeProductTypes as BottleSizeProductTypesPayload['data'])
                  : [],
              );

              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{name ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{productType ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{lidSize ? `${lidSize} mm` : '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{cc ?? '--'}</TableCell>
                  <TableCell sx={textWhiteSpace}>{oz ?? '--'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                      color={isActive ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={!!isActive}
                      editRoute={EDIT_BOTTLE_SIZE_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default BottleSizeTable;
